import MDEditor from '@uiw/react-md-editor'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Code, Plus, Save, Table2, Trash2 } from 'lucide-react'
import { Column, Row } from 'src/shared/components/Semantic/all'
import { Button } from 'src/shared/components/ui/button'
import { Input } from 'src/shared/components/ui/input'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from 'src/shared/components/ui/collapsible'
import { Card } from 'src/shared/components/ui/card'
import {
  ToggleGroup,
  ToggleGroupItem,
} from 'src/shared/components/ui/toggle-group'
import { ChevronDown } from 'lucide-react'

import { RawTableEditor } from './RawTableEditor'
import {
  parseJson,
  printCodesToStr,
  strToPrintCodes,
  toJsonStr,
  toStr,
} from '../utils'
import { DecorationInfo_ } from 'src/api/db/apiDecorationInfo'

export const DecorationInfoTable = ({
  data,
  printCode,
  isLoading,
  onSave,
  onAdd,
  onDelete,
}: {
  data: DecorationInfo_[]
  printCode?: string | null
  isLoading: boolean
  onSave: (
    updates: Array<{
      id: number
      name?: string | null
      one_liner?: string | null
      subtitle?: string | null
      link?: string | null
      video_url?: string | null
      icon_url?: string | null
      content?: unknown
      image_urls?: unknown
      print_codes?: string[] | null
    }>
  ) => Promise<void>
  onAdd: (insert: {
    name?: string | null
    one_liner?: string | null
    subtitle?: string | null
    link?: string | null
    video_url?: string | null
    icon_url?: string | null
    content?: unknown
    image_urls?: unknown
    print_codes?: string[] | null
  }) => Promise<void>
  onDelete: (id: number) => Promise<void>
}) => {
  const nextId = useRef(-1)
  const [entryMode, setEntryMode] = useState<'table' | 'raw'>('table')
  const [rows, setRows] = useState<
    Array<{
      id: number
      name: string
      one_liner: string
      subtitle: string
      link: string
      video_url: string
      icon_url: string
      content: string
      image_urls: string
      print_codes: string
      created_at: string
    }>
  >([])
  const [isSaving, setIsSaving] = useState(false)

  useEffect(() => {
    setRows(
      data.map((r) => ({
        id: r.id,
        name: toStr(r.name),
        one_liner: toStr(r.one_liner),
        subtitle: toStr(r.subtitle),
        link: toStr(r.link),
        video_url: toStr(r.video_url),
        icon_url: toStr(r.icon_url),
        content: toJsonStr(r.content),
        image_urls: toJsonStr(r.image_urls),
        print_codes: printCodesToStr(r.print_codes),
        created_at: r.created_at,
      }))
    )
  }, [data])

  const update = useCallback(
    (i: number, field: string, value: string) =>
      setRows((prev) =>
        prev.map((r, j) => (j === i ? { ...r, [field]: value } : r))
      ),
    []
  )

  const add = useCallback(
    () =>
      setRows((prev) => [
        ...prev,
        {
          id: nextId.current--,
          name: '',
          one_liner: '',
          subtitle: '',
          link: '',
          video_url: '',
          icon_url: '',
          content: '',
          image_urls: '',
          print_codes: printCode ?? '',
          created_at: '',
        },
      ]),
    [printCode]
  )

  const remove = useCallback(
    async (i: number) => {
      const r = rows[i]
      if (r?.id > 0 && onDelete) await onDelete(r.id)
      setRows((prev) => prev.filter((_, j) => j !== i))
    },
    [rows, onDelete]
  )

  const save = useCallback(async () => {
    setIsSaving(true)
    try {
      const toUpdate = rows
        .filter((r) => r.id > 0)
        .map((r) => ({
          id: r.id,
          name: r.name.trim() || null,
          one_liner: r.one_liner.trim() || null,
          subtitle: r.subtitle.trim() || null,
          link: r.link.trim() || null,
          video_url: r.video_url.trim() || null,
          icon_url: r.icon_url.trim() || null,
          content: r.content?.trim() || null,
          image_urls: parseJson(r.image_urls),
          print_codes: strToPrintCodes(r.print_codes) ?? null,
        }))
      if (toUpdate.length) await onSave(toUpdate)
      if (onAdd) {
        for (const r of rows.filter((x) => x.id < 0)) {
          await onAdd({
            name: r.name.trim() || null,
            one_liner: r.one_liner.trim() || null,
            subtitle: r.subtitle.trim() || null,
            link: r.link.trim() || null,
            video_url: r.video_url.trim() || null,
            icon_url: r.icon_url.trim() || null,
            content: parseJson(r.content),
            image_urls: parseJson(r.image_urls),
            print_codes:
              strToPrintCodes(r.print_codes) ??
              strToPrintCodes(printCode ?? '') ??
              null,
          })
        }
      }
    } finally {
      setIsSaving(false)
    }
  }, [rows, onSave, onAdd, printCode])

  const rawInitialValue = useMemo(
    () =>
      `[\n${data
        .map((r) =>
          JSON.stringify(
            {
              id: r.id,
              name: r.name,
              one_liner: r.one_liner,
              subtitle: r.subtitle,
              link: r.link,
              video_url: r.video_url,
              icon_url: r.icon_url,
              print_codes: r.print_codes,
              content: r.content,
              image_urls: r.image_urls,
            },
            null,
            2
          )
        )
        .join(',\n')}\n]`,
    [data]
  )

  const handleRawSave = useCallback(
    async (parsed: Record<string, unknown>[]) => {
      const existingIds = new Set(data.map((r) => r.id))
      const parsedIds = new Set(
        parsed.map((r) => Number(r?.id)).filter((id) => id > 0)
      )
      const toUpdate = parsed
        .filter((r) => Number(r?.id) > 0)
        .map((r) => ({
          id: Number(r.id),
          name: (r.name as string) ?? null,
          one_liner: (r.one_liner as string) ?? null,
          subtitle: (r.subtitle as string) ?? null,
          link: (r.link as string) ?? null,
          video_url: (r.video_url as string) ?? null,
          icon_url: (r.icon_url as string) ?? null,
          print_codes: Array.isArray(r.print_codes)
            ? r.print_codes
            : typeof r.print_codes === 'string'
            ? strToPrintCodes(r.print_codes)
            : null,
          content: r.content ?? null,
          image_urls: r.image_urls ?? null,
        }))
      const toCreate = parsed
        .filter((r) => !r?.id || Number(r.id) <= 0)
        .map((r) => ({
          name: (r.name as string) ?? null,
          one_liner: (r.one_liner as string) ?? null,
          subtitle: (r.subtitle as string) ?? null,
          link: (r.link as string) ?? null,
          video_url: (r.video_url as string) ?? null,
          icon_url: (r.icon_url as string) ?? null,
          print_codes: Array.isArray(r.print_codes)
            ? r.print_codes
            : typeof r.print_codes === 'string'
            ? strToPrintCodes(r.print_codes)
            : strToPrintCodes(printCode ?? ''),
          content: r.content ?? null,
          image_urls: r.image_urls ?? null,
        }))
      const toDelete = [...existingIds].filter((id) => !parsedIds.has(id))
      if (toUpdate.length) await onSave(toUpdate)
      for (const r of toCreate) await onAdd(r)
      for (const id of toDelete) await onDelete(id)
    },
    [data, printCode, onSave, onAdd, onDelete]
  )

  return (
    <Card>
      <Collapsible defaultOpen>
        <CollapsibleTrigger asChild>
          <Button variant="ghost" className="w-full justify-between px-4 py-2">
            <span className="font-medium">Decoration info</span>
            <ChevronDown className="h-4 w-4" />
          </Button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <Column className="gap-4 pt-2">
            <Row className="items-center gap-2">
              <span className="text-sm text-muted-foreground">Edit mode:</span>
              <ToggleGroup
                type="single"
                value={entryMode}
                onValueChange={(v) => v && setEntryMode(v as 'table' | 'raw')}
                variant="outline"
              >
                <ToggleGroupItem value="table">
                  <Table2 className="h-4 w-4 mr-2" />
                  Table
                </ToggleGroupItem>
                <ToggleGroupItem value="raw">
                  <Code className="h-4 w-4 mr-2" />
                  Raw
                </ToggleGroupItem>
              </ToggleGroup>
            </Row>

            {entryMode === 'raw' ? (
              <RawTableEditor
                initialValue={rawInitialValue}
                dataKey={data}
                onSave={handleRawSave}
              />
            ) : (
              <>
                <Column className="gap-6">
                  {rows.length === 0 && !isLoading ? (
                    <p className="text-center text-muted-foreground py-6">
                      No rows
                    </p>
                  ) : (
                    rows.map((row, i) => (
                      <Column
                        key={row.id}
                        className="gap-3 border rounded-md p-4"
                      >
                        <Row className="items-center justify-between">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => remove(i)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </Row>

                        <Input
                          label="Print codes"
                          value={row.print_codes}
                          onChange={(e) =>
                            update(i, 'print_codes', e.target.value)
                          }
                        />

                        <Input
                          label="Name"
                          value={row.name}
                          onChange={(e) => update(i, 'name', e.target.value)}
                        />

                        <Input
                          label="One liner"
                          value={row.one_liner}
                          onChange={(e) =>
                            update(i, 'one_liner', e.target.value)
                          }
                        />

                        <Input
                          label="Subtitle"
                          value={row.subtitle}
                          onChange={(e) =>
                            update(i, 'subtitle', e.target.value)
                          }
                        />

                        <Input
                          label="Link"
                          value={row.link}
                          onChange={(e) => update(i, 'link', e.target.value)}
                        />

                        <Input
                          label="Video URL"
                          value={row.video_url}
                          onChange={(e) =>
                            update(i, 'video_url', e.target.value)
                          }
                        />

                        <Input
                          label="Icon URL"
                          value={row.icon_url}
                          onChange={(e) =>
                            update(i, 'icon_url', e.target.value)
                          }
                        />

                        <Input
                          label="Image URLs (JSON)"
                          value={row.image_urls}
                          onChange={(e) =>
                            update(i, 'image_urls', e.target.value)
                          }
                          placeholder='["url1", "url2"]'
                          className="font-mono text-xs"
                        />

                        <Column className="gap-1">
                          <label className="text-sm font-medium text-muted-foreground">
                            Content
                          </label>
                          <div data-color-mode="light">
                            <MDEditor
                              value={row.content ?? ''}
                              onChange={(v) => update(i, 'content', v ?? '')}
                              height={200}
                              preview="live"
                            />
                          </div>
                        </Column>
                      </Column>
                    ))
                  )}
                </Column>
                <Row className="gap-2">
                  <Button variant="secondary" size="sm" onClick={add}>
                    <Plus className="h-4 w-4 mr-2" />
                    Add row
                  </Button>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={save}
                    disabled={isSaving}
                  >
                    <Save className="h-4 w-4 mr-2" />
                    Save
                  </Button>
                </Row>
              </>
            )}
          </Column>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  )
}
